import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowRight,
  Mail,
  Linkedin,
  Twitter,
  Users,
} from "lucide-react";
import PageShell from "@/components/site/PageShell";
import FinalCTA from "@/components/site/FinalCTA";

const FOUNDER_IMG =
  "https://customer-assets.emergentagent.com/job_echowings-premium/artifacts/zurefzue_image.png";

const TEAM = [
  {
    slug: "matthew-burgess",
    name: "Matthew Burgess",
    role: "Founder & CEO",
    photo: FOUNDER_IMG,
    bio: "With over a decade of experience in web development, branding, and digital strategy, Matthew leads EchoWings Media with a mission to help businesses thrive in the digital world.",
    points: [
      "Transforms complex digital challenges into clear, actionable strategies",
      "Focuses on transparent, measurable results",
      "Works closely with business owners to bring their vision to life",
    ],
    email: "info@echowingsmedia.com",
    tags: ["Strategy", "Web Dev", "Branding"],
  },
  {
    slug: "arvind-pal",
    name: "Arvind Pal",
    role: "Head of Operations",
    photo:
      "https://images.unsplash.com/photo-1560250097-0b93528c311a?auto=format&fit=crop&q=80&w=800",
    bio: "Arvind keeps the EchoWings studio running like a machine — orchestrating resourcing, timelines and quality control so every project ships on time and to standard.",
    points: [
      "Orchestrates every project from kickoff to launch",
      "Guards timelines, budgets and quality standards",
      "Owns the client success experience end-to-end",
    ],
    email: "info@echowingsmedia.com",
    tags: ["Operations", "Delivery", "Client Success"],
  },
  {
    slug: "ashley-olson",
    name: "Ashley Olson",
    role: "Client Services Manager",
    photo:
      "https://images.unsplash.com/photo-1580489944761-15a19d654956?auto=format&fit=crop&q=80&w=800",
    bio: "Ashley is the bridge between clients, sales reps and the EchoWings team — managing onboarding, communication and scheduling so every client feels informed and supported from day one.",
    points: [
      "Owns client onboarding, communication and sales-rep support",
      "Keeps leads, sales, payments and projects updated in the dashboard",
      "Weekly client check-ins &amp; personal assistant to Matthew",
    ],
    email: "info@echowingsmedia.com",
    tags: ["Client Services", "Communication", "PA"],
  },
];

const VALUES = [
  { label: "Ownership", desc: "Everyone on the team owns outcomes, not just tasks." },
  { label: "Craft", desc: "We sweat the details — pixels, code and copy." },
  { label: "Transparency", desc: "You see the metrics we see, in real time." },
  { label: "Speed", desc: "We ship fast without cutting corners." },
];

export default function TeamPage() {
  return (
    <PageShell testId="team-page">
      {/* Hero */}
      <section className="relative pt-16 pb-12 overflow-hidden">
        <div className="absolute inset-0 grid-bg opacity-30 pointer-events-none" />
        <div className="absolute -top-32 left-1/2 -translate-x-1/2 w-[60rem] h-[60rem] radial-glow opacity-40 pointer-events-none" />
        <div className="relative max-w-4xl mx-auto px-6 md:px-12 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-cyan-500/10 border border-cyan-500/20 text-cyan-300 text-xs font-semibold tracking-widest uppercase">
              Meet Our Team
            </div>
            <h1 className="mt-6 font-display text-5xl md:text-6xl font-extrabold tracking-tight leading-[1.05]">
              A small team.{" "}
              <span className="text-gradient-brand">Senior across the board.</span>
            </h1>
            <p className="mt-6 text-lg text-white/65 max-w-2xl mx-auto">
              No juniors, no account layers, no hand-offs. Every EchoWings
              engagement is led by the people you meet on this page.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Team grid */}
      <section className="section pt-4" data-testid="team-grid">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {TEAM.map((m, i) => (
              <motion.article
                key={m.slug}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.15 }}
                transition={{ duration: 0.55, delay: i * 0.08 }}
                data-testid={`team-card-${i}`}
                className="group relative rounded-3xl border border-white/10 bg-white/[0.025] hover:border-cyan-500/30 hover:bg-white/[0.04] transition-all duration-500 overflow-hidden flex flex-col"
              >
                <div className="relative m-3 mb-0 rounded-2xl overflow-hidden bg-[#0a0a0a] border border-white/5">
                  <div className="aspect-[4/5] overflow-hidden">
                    <img
                      src={m.photo}
                      alt={m.name}
                      className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                      loading="lazy"
                    />
                  </div>
                  <div className="absolute inset-x-0 bottom-0 h-24 bg-gradient-to-t from-black/70 to-transparent pointer-events-none" />
                </div>

                <div className="p-6 flex flex-col flex-1">
                  <div className="text-[10px] uppercase tracking-[0.18em] font-semibold text-cyan-400">
                    {m.role}
                  </div>
                  <h3 className="mt-2 font-display text-2xl font-bold">
                    {m.name}
                  </h3>

                  <p className="mt-3 text-sm text-white/60 leading-relaxed min-h-[4.5rem]">
                    {m.bio}
                  </p>

                  <ul className="mt-5 space-y-2 flex-1">
                    {m.points.map((p) => (
                      <li
                        key={p}
                        className="flex items-start gap-2 text-sm text-white/75"
                      >
                        <span className="mt-2 w-1.5 h-1.5 rounded-full bg-cyan-400 shrink-0" />
                        <span
                          dangerouslySetInnerHTML={{ __html: p }}
                        />
                      </li>
                    ))}
                  </ul>

                  <div className="mt-5 flex flex-wrap gap-1.5">
                    {m.tags.map((t) => (
                      <span
                        key={t}
                        className="text-[10px] uppercase tracking-widest px-2 py-1 rounded-full bg-white/5 border border-white/10 text-white/60 font-semibold"
                      >
                        {t}
                      </span>
                    ))}
                  </div>

                  <div className="mt-6 pt-4 border-t border-white/5 flex items-center justify-between">
                    <a
                      href={`mailto:${m.email}`}
                      data-testid={`team-email-${i}`}
                      className="inline-flex items-center gap-2 text-sm font-semibold text-cyan-400 hover:text-cyan-300 transition-colors"
                    >
                      <Mail className="w-4 h-4" /> Email
                    </a>
                    <div className="flex items-center gap-1.5">
                      <a
                        href="#"
                        aria-label="LinkedIn"
                        data-testid={`team-linkedin-${i}`}
                        className="w-8 h-8 rounded-full bg-white/5 border border-white/10 flex items-center justify-center text-white/70 hover:text-cyan-400 hover:border-cyan-500/40 transition-colors"
                      >
                        <Linkedin className="w-3.5 h-3.5" />
                      </a>
                      <a
                        href="#"
                        aria-label="Twitter"
                        data-testid={`team-twitter-${i}`}
                        className="w-8 h-8 rounded-full bg-white/5 border border-white/10 flex items-center justify-center text-white/70 hover:text-cyan-400 hover:border-cyan-500/40 transition-colors"
                      >
                        <Twitter className="w-3.5 h-3.5" />
                      </a>
                    </div>
                  </div>
                </div>
              </motion.article>
            ))}
          </div>
        </div>
      </section>

      {/* Inside the Studio — split section */}
      <section
        className="section pt-0"
        data-testid="team-studio"
      >
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="relative rounded-[2rem] border border-white/10 bg-[#0a0a0a] overflow-hidden">
            <div className="grid lg:grid-cols-12 gap-0 items-stretch">
              {/* Left — image + floating tags */}
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true, amount: 0.15 }}
                transition={{ duration: 0.7 }}
                className="relative lg:col-span-6 min-h-[420px] lg:min-h-[600px]"
              >
                <img
                  src="https://images.unsplash.com/photo-1522071820081-009f0129c71c?auto=format&fit=crop&q=80&w=1400"
                  alt="Inside the EchoWings studio"
                  className="absolute inset-0 w-full h-full object-cover"
                  loading="lazy"
                />
                <div className="absolute inset-0 bg-gradient-to-tr from-black/80 via-black/40 to-transparent lg:bg-gradient-to-r lg:from-transparent lg:via-transparent lg:to-[#0a0a0a]" />

                {/* Floating badges */}
                <div className="absolute top-6 left-6 glass-card rounded-2xl px-4 py-3 flex items-center gap-3">
                  <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-blue-600 to-cyan-400 flex items-center justify-center text-black">
                    <Users className="w-4 h-4" />
                  </div>
                  <div>
                    <div className="text-[9px] uppercase tracking-widest text-white/50 font-semibold">
                      Senior team
                    </div>
                    <div className="font-semibold text-white text-sm">
                      100% seniors
                    </div>
                  </div>
                </div>

                <div className="absolute bottom-6 left-6 glass-card rounded-2xl px-4 py-3">
                  <div className="text-[9px] uppercase tracking-widest text-cyan-300 font-semibold">
                    Grand Rapids, MI · Remote-first
                  </div>
                  <div className="mt-1 font-display text-base font-semibold text-white">
                    EchoWings Studio
                  </div>
                </div>

                <div className="absolute top-6 right-6 hidden md:block glass-card rounded-2xl px-4 py-3 text-center">
                  <div className="font-display text-2xl font-extrabold text-gradient-brand">
                    98%
                  </div>
                  <div className="text-[9px] uppercase tracking-widest text-white/50 font-semibold">
                    Client retention
                  </div>
                </div>
              </motion.div>

              {/* Right — culture + values + CTA */}
              <motion.div
                initial={{ opacity: 0, x: 20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true, amount: 0.15 }}
                transition={{ duration: 0.7, delay: 0.1 }}
                className="lg:col-span-6 p-8 md:p-12 lg:p-14 flex flex-col"
              >
                <div className="inline-flex self-start items-center gap-2 px-3 py-1 rounded-full bg-cyan-500/10 border border-cyan-500/20 text-cyan-300 text-xs font-semibold tracking-widest uppercase">
                  Inside The Studio
                </div>
                <h2 className="mt-5 font-display text-3xl md:text-4xl lg:text-5xl font-bold tracking-tight leading-[1.05]">
                  A small studio, obsessed with{" "}
                  <span className="text-gradient-brand">craft.</span>
                </h2>
                <p className="mt-5 text-white/60 leading-relaxed">
                  We keep the team deliberately small so every project gets
                  senior attention from kickoff to launch. No handoffs. No
                  ticket queues. Just operators who care about your outcome.
                </p>

                <ul className="mt-8 space-y-4">
                  {VALUES.map((v, i) => (
                    <li
                      key={v.label}
                      data-testid={`team-value-${i}`}
                      className="flex items-start gap-4"
                    >
                      <div className="shrink-0 w-10 h-10 rounded-xl bg-gradient-to-br from-blue-600/20 to-cyan-400/10 border border-white/10 flex items-center justify-center font-display font-bold text-cyan-300 text-sm">
                        {String(i + 1).padStart(2, "0")}
                      </div>
                      <div>
                        <div className="font-display text-lg font-semibold text-white">
                          {v.label}
                        </div>
                        <p className="mt-0.5 text-sm text-white/55 leading-relaxed">
                          {v.desc}
                        </p>
                      </div>
                    </li>
                  ))}
                </ul>

                <div className="mt-10 pt-8 border-t border-white/10 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-5">
                  <div>
                    <div className="text-[10px] uppercase tracking-widest text-cyan-300 font-semibold">
                      Careers
                    </div>
                    <div className="mt-1 font-display text-xl font-semibold">
                      Want to build here with us?
                    </div>
                  </div>
                  <Link
                    to="/contact"
                    data-testid="team-careers-cta"
                    className="inline-flex items-center gap-2 bg-gradient-to-r from-blue-600 to-cyan-400 text-black font-semibold px-6 py-3.5 rounded-full hover:shadow-[0_0_30px_rgba(0,229,255,0.4)] transition-all whitespace-nowrap"
                  >
                    Get in touch <ArrowRight className="w-4 h-4" />
                  </Link>
                </div>
              </motion.div>
            </div>
          </div>
        </div>
      </section>

      <FinalCTA />
    </PageShell>
  );
}
